"use client";

import Link from 'next/link';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { WHATSAPP_URL } from '@/lib/whatsapp';

const features = [
  {
    imageSrc: '/images/features/dashboard.png',
    title: 'Dashboard de Administración',
    text: 'Gestiona los servicios que ofreces, crea, actualiza y cancela citas, gestiona otros profesionales, todo desde nuestro dashboard.',
  },
  {
    imageSrc: '/images/features/perfil.png',
    title: 'Perfil de tu Negocio',
    text: 'Crea y configura el perfil de tu negocio, luce más profesional y expónte ante cientos de usuarios de nuestra plataforma.',
  },
  {
    imageSrc: '/images/features/recordatorio.png',
    title: 'Recordatorios y confirmaciones',
    text: 'Envía recordatorios por WhatsApp desde la agenda cuando lo necesites y consulta el estado de cada cita.',
  },
  {
    imageSrc: '/images/features/coexistence.png',
    title: 'Controla el Asistente',
    text: 'Sabemos que los clientes suelen hacer preguntas concretas. Responde personalmente cuando lo necesites. Luego el asistente retoma la conversación.',
  },
  {
    imageSrc: '/images/features/horarios-descanso-ausencias.png',
    title: 'Horarios, descansos y ausencias',
    text: 'Bloquea días, pausas y excepciones para que el asistente no prometa turnos que el equipo no puede atender.',
  },
  {
    imageSrc: '/images/features/clientes.png',
    title: 'Clientes con historial',
    text: 'Revisa estadísticas de tus clientes, información de contacto, cantidad de citas programadas, entre otros.',
  },
];

const dashboardImageSrc = '';

function FeatureThumbnail({ src }: { src: string }) {
  if (!src) {
    return <div className="aspect-[16/7] w-full bg-accent/10" aria-hidden="true" />;
  }

  return (
    <div className="relative aspect-[16/7] w-full">
      <Image
        src={src}
        alt=""
        fill
        sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
        className="object-contain"
      />
    </div>
  );
}

export function Features() {
  const [carouselRef, carouselApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!carouselApi) return;

    setCanScrollPrev(carouselApi.canScrollPrev());
    setCanScrollNext(carouselApi.canScrollNext());
  }, [carouselApi]);

  const scrollPrev = useCallback(() => carouselApi?.scrollPrev(), [carouselApi]);
  const scrollNext = useCallback(() => carouselApi?.scrollNext(), [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;

    updateScrollState();
    carouselApi.on('select', updateScrollState);
    carouselApi.on('reInit', updateScrollState);

    return () => {
      carouselApi.off('select', updateScrollState);
      carouselApi.off('reInit', updateScrollState);
    };
  }, [carouselApi, updateScrollState]);

  return (
    <section id="funciones" data-section="features" className="bg-bg-primary py-20 sm:py-24">
      <div className="container mx-auto px-6">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr_auto] lg:items-end">
          <div>
            <Badge variant="outline" className="mb-5">
              Agenda sin teatro tecnológico
            </Badge>
            <h2 className="max-w-3xl font-heading text-3xl font-bold leading-tight text-text-primary sm:text-5xl">
              Características de OficioYa
            </h2>
          </div>
          <div>
            <p className="text-base leading-8 text-text-secondary">
            </p>
          </div>
          <div className="hidden items-center gap-2 lg:flex">
            <button
              type="button"
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="inline-flex h-11 w-11 items-center justify-center rounded-button border border-border text-text-primary transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-border disabled:hover:text-text-primary"
              aria-label="Funcion anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="inline-flex h-11 w-11 items-center justify-center rounded-button border border-border text-text-primary transition-colors hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-border disabled:hover:text-text-primary"
              aria-label="Siguiente funcion"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="mt-12 overflow-hidden">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-24 bg-gradient-to-l from-bg-primary to-transparent lg:block" aria-hidden="true" />
            <div ref={carouselRef} className="overflow-hidden">
              <div className="flex cursor-grab gap-5 active:cursor-grabbing">
                {features.map((feature) => (
                  <article
                    key={feature.title}
                    className="group min-w-0 flex-[0_0_80%] overflow-hidden rounded-card border border-border bg-bg-secondary/68 transition-colors hover:border-accent/45 hover:bg-bg-secondary lg:flex-[0_0_45%]"
                  >
                    <FeatureThumbnail src={feature.imageSrc} />
                    <div className="p-5">
                      <h3 className="font-heading text-xl font-semibold leading-tight text-text-primary">{feature.title}</h3>
                      <p className="mt-3 text-sm leading-7 text-text-secondary">{feature.text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* <div className="mt-10 grid gap-4 rounded-card border border-accent/30 bg-accent/10 p-5 sm:grid-cols-[auto_1fr_auto] sm:items-center">
          <div className="w-full overflow-hidden rounded-card bg-accent/15 sm:w-28">
            <FeatureThumbnail src={dashboardImageSrc} />
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold text-text-primary">Y sí, también tienes panel de control.</h3>
            <p className="mt-1 text-sm leading-6 text-text-secondary">
              Pero el cliente no tiene que entrar a él. Esa es la diferencia.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button variant="secondary" asChild>
              <Link href="/funciones/">
                Ver funciones
              </Link>
            </Button>
            <Button asChild>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                Hablar por WhatsApp
              </a>
            </Button>
          </div>
        </div> */}
      </div>
    </section>
  );
}
